import { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPerformanceInterviewAndFeedbackAction, getSummaryAction, previewDocumentsAction } from "../actions";
import { getLoading, getData, getSummaryData } from "../selectors";
import { getSocietySelected } from "@icarius-pages/login/selectors";
import { getUserData } from "@icarius-app/selectors";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { getFeedbackAction, getFeedbackPrivacyOptionsAction } from "@icarius-pages/feedback/actions";
import {
    getIsLoadingPrivacyData,
    getTypeList,
    getModeList,
    getPeople,
    getGroups,
    getTasks,
    getGoals,
    getIsLoadingGridData,
    getIsLoadingFeed,
    getComments,
    getIsLoadingComments,
} from "@icarius-pages/feedback/selectors";
import { getLocalizedString } from "@icarius-localization/strings";
import { getIsLoading } from "@icarius-common/medalTable/selectors";

const usePerformanceInterviewAndFeedback = () => {
    const [selectedProcessCode, setSelectedProcessCode] = useState(null); // se guarda solo el code facilitar la actualizacion de datos despues de hacer el acceptReject
    const [selectedEvaluation, setSelectedEvaluation] = useState(null);
    const [feedbackFormData, setFeedbackFormData] = useState(null);
    const [viewFeedbackIsOpen, setViewFeedbackIsOpen] = useState(false);
    const [codeForMedalTable, setCodeForMedalTable] = useState(null);

    const dispatch = useDispatch();
    const userData = useSelector(getUserData);
    const data = useSelector(getData);
    const summaryData = useSelector(getSummaryData);
    const isLoading = useSelector(getLoading);
    const societySelected = useSelector(getSocietySelected);

    const { name, level, code } = userData;

    const selectedProcess = useMemo(() => {
        if (!selectedProcessCode || !data) return null;
        return data.find((item) => item.process === selectedProcessCode);
    }, [data, selectedProcessCode]);

    //feedback
    const typeList = useSelector(getTypeList);
    const modeList = useSelector(getModeList);
    const people = useSelector(getPeople);
    const groups = useSelector(getGroups);
    const tasks = useSelector(getTasks);
    const goals = useSelector(getGoals);
    const comments = useSelector(getComments);

    const isLoadingPrivacyData = useSelector(getIsLoadingPrivacyData);
    const isLoadingGridData = useSelector(getIsLoadingGridData);
    const isLoadingFeed = useSelector(getIsLoadingFeed);
    const isLoadingComments = useSelector(getIsLoadingComments);
    const isLoadingMedalTable = useSelector(getIsLoading);

    const isLoadingFeedback = {
        gridData: isLoadingGridData,
        privacyData: isLoadingPrivacyData,
        feed: isLoadingFeed,
        comments: isLoadingComments,
    };

    useEffect(() => {
        dispatch(getPerformanceInterviewAndFeedbackAction()).then((resp) => {
            if (resp.status === 200) {
                dispatch(getFeedbackAction(false)).then((resp) => {
                    if (resp.status === 200) {
                        dispatch(getFeedbackPrivacyOptionsAction()).then((resp) => {
                            if (resp.status === 200) {
                            }
                        });
                    }
                });
            }
        });
    }, [dispatch]);

    const handleSelectProcess = (data) => {
        setSelectedProcessCode(data.process);
    };

    const handleUnselectProcess = () => {
        setSelectedProcessCode(null);
    };

    const handleOpenSummary = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        dispatch(getSummaryAction(selectedProcess.process, selectedRows[0].evaluatedCode)).then((resp) => {
            if (resp.status === 200) {
                setSelectedEvaluation(selectedRows[0]);
            }
        });
    };

    const handleCloseSummary = () => {
        setSelectedEvaluation(null);
    };

    const handleOpenMedalTable = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        if (!selectedRows[0].recognitionPlan) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("employeeMustBeInPlan"), severity: "warning" }));
            return;
        }

        setCodeForMedalTable(selectedRows[0].evaluatedCode);
    };

    const handleCloseMedalTable = useCallback(() => {
        setCodeForMedalTable(false);
    }, []);

    const handleOpenFeedbackForm = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        if (selectedRows[0].evaluatedCode === code) {
            dispatch(
                openSnackbarAction({
                    msg: getLocalizedString("youCannotSendFeedbackForAnEvaluationYouAreBeingEvaluatedIn"),
                    severity: "warning",
                })
            );
            return;
        }

        if (selectedRows[0].noteStatus !== getLocalizedString("final")) {
            dispatch(
                openSnackbarAction({
                    msg: getLocalizedString("feedbackCanOnlyBeGivenIfGradeStatusIsFinal"),
                    severity: "warning",
                })
            );
            return;
        }

        if (!["K"].includes(selectedProcess.processStatus)) {
            dispatch(
                openSnackbarAction({ msg: getLocalizedString("evaluationMustBeInFeedbackState"), severity: "warning" })
            );
            return;
        }

        if (selectedRows[0].feedback !== getLocalizedString("no")) {
            dispatch(
                openSnackbarAction({
                    msg: getLocalizedString("selectedEvaluationAlreadyHasAssociatedFeedback"),
                    severity: "warning",
                })
            );
            return;
        }

        setFeedbackFormData(selectedRows[0]);
    };

    const handleCloseFeedbackForm = () => {
        setFeedbackFormData(null);
    };

    const handleOpenViewFeedback = () => {
        setViewFeedbackIsOpen(true);
    };

    const handleCloseViewFeedback = () => {
        setViewFeedbackIsOpen(false);
    };

    const handleCreateFeedbackCallback = () => {
        dispatch(getPerformanceInterviewAndFeedbackAction());
    };

    const handleDownloadSummary = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }
        const data = { process: selectedProcess.process, evaluatedCode: selectedRows[0].evaluatedCode };
        dispatch(previewDocumentsAction(data));
    };

    const state = {
        societySelected,
        selectedProcess,
        selectedEvaluation,
        codeForMedalTable,
        feedbackFormData,
        viewFeedbackIsOpen,
    };

    const handlers = {
        handleSelectProcess,
        handleUnselectProcess,
        handleOpenSummary,
        handleCloseSummary,
        handleOpenFeedbackForm,
        handleCloseFeedbackForm,
        handleCreateFeedbackCallback,
        handleOpenViewFeedback,
        handleCloseViewFeedback,
        handleDownloadSummary,
        handleOpenMedalTable,
        handleCloseMedalTable,
    };

    return {
        name,
        level,
        data,
        summaryData,
        isLoading,
        state,
        handlers,

        //feedback
        typeList,
        modeList,
        people,
        groups,
        tasks,
        goals,
        comments,
        isLoadingFeedback,
        isLoadingMedalTable,
    };
};

export default usePerformanceInterviewAndFeedback;
